import type { HttpErrorResponse } from '@angular/common/http';

import { createEntityCollectionStore, pipeState } from '@core/ngrx-legacy';
import type { ReferralJobTemplateAssignment } from '@core/referrals-types';

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<ReferralJobTemplateAssignment>()(
    'ReferralsJobTemplateAssignmentCollection',
    {
      idSelector: e => e.offerTemplate.id,
      customActions: adapter => ({
        fetchOne: (state, payload: { id: number }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
        fetchOneSuccess: (state, payload: { data: ReferralJobTemplateAssignment }) =>
          pipeState(adapter.upsertOne(state, payload.data), next =>
            adapter.markAsLoaded(next, { scope: null, entities: [payload.data.offerTemplate.id] }),
          ),
        toggle: (state, payload: { jobTemplateId: number; enabled: boolean }) =>
          pipeState(
            adapter.markAsPending(state, { scope: null, entities: [payload.jobTemplateId] }),
          ),
        toggleSuccess: (state, payload: { data: Partial<ReferralJobTemplateAssignment> }) =>
          pipeState(adapter.updateOne(state, payload.data), next =>
            adapter.markAsLoaded(next, {
              scope: null,
              entities: [payload.data.offerTemplate?.id!],
            }),
          ),
        toggleFailed: (state, payload: { jobTemplateId: number; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, {
            scope: null,
            entities: [payload.jobTemplateId],
          }),
        assignReward: (
          state,
          payload: { jobTemplateId: number; referralRewardId: number | null },
        ) =>
          pipeState(
            adapter.markAsPending(state, { scope: null, entities: [payload.jobTemplateId] }),
          ),
        assignRewardSuccess: (state, payload: { data: Partial<ReferralJobTemplateAssignment> }) =>
          pipeState(adapter.updateOne(state, payload.data), next =>
            adapter.markAsLoaded(next, {
              scope: null,
              entities: [payload!.data.offerTemplate?.id!],
            }),
          ),
        assignRewardFailed: (state, payload: { jobTemplateId: number; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, {
            scope: null,
            entities: [payload.jobTemplateId],
          }),
        assignTemplate: (state, payload: { jobTemplateId: number; templateId: number | null }) =>
          pipeState(
            adapter.markAsPending(state, { scope: null, entities: [payload.jobTemplateId] }),
          ),
        assignTemplateSuccess: (
          state,
          payload: { data: Partial<ReferralJobTemplateAssignment> },
        ) => {
          let entity = payload.data;
          entity = { ...entity };
          entity.referralTemplate = entity.referralTemplate ?? null;
          return pipeState(adapter.updateOne(state, entity), next =>
            adapter.markAsLoaded(next, {
              scope: null,
              entities: [payload!.data.offerTemplate?.id!],
            }),
          );
        },
        assignTemplateFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
      }),
    },
  );

export {
  actions as ReferralsJobTemplateAssignmentActions,
  reducer as ReferralsJobTemplateAssignmentReducer,
  selectors as ReferralsJobTemplateAssignmentSelectors,
  namespace as ReferralsJobTemplateAssignmentNamespace,
};
